import { addMinutes, format } from 'date-fns';
import { useMemo } from 'react';

import { faCalendar } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

import confettiLottie from 'src/assets/animations/Confetti.lottie?url';
import HeartImage from 'src/assets/images/visit-summary-heart.avif';
import PageFooter from 'src/components/PageFooter';
import { useTeamMemberData } from 'src/hooks/useTeamMemberData';
import { InnerMaxWidthContainer } from 'src/layouts/InnerMaxWidthContainer';
import { useGetVisitsQuery } from 'src/store/sageHomeApi';
import { getPronoun, getTeamMemberDisplayName } from 'src/utils/nameUtils';

export const Route = createFileRoute('/(app)/visits/summary/success')({
    component: VisitSummarySuccessComponent,
});

const formatVisitDateRange = (visit?: {
    visitStartInstant?: number | null;
    visitDurationMinutes?: number | null;
}) => {
    if (!visit?.visitStartInstant) return undefined;

    const start = new Date(visit.visitStartInstant);
    const end = addMinutes(start, visit.visitDurationMinutes ?? 60);

    const datePart = format(start, 'MMMM d');
    const timePart = `${format(start, 'p')} \u2013 ${format(end, 'p')}`;

    return `${datePart}, ${timePart}`;
};

const FUTURE_VISIT_BOUNDARY = 1000 * 60 * 60 * 24 * 30; // 30 days

function VisitSummarySuccessComponent() {
    const { careRecipient, currentUser } = useTeamMemberData();
    const { palette } = useTheme();
    const navigate = useNavigate();
    const nameSuffix = currentUser
        ? ` ${getTeamMemberDisplayName(currentUser, { firstOnly: true })}`
        : '';

    const careRecipientName = getTeamMemberDisplayName(careRecipient, { firstOnly: true });
    const careRecipientPossessive =
        careRecipientName && careRecipientName.length > 0
            ? `${careRecipientName}${careRecipientName.endsWith('s') ? "'" : "'s"}`
            : `${getPronoun(careRecipient?.pronouns, 'possessive') ?? 'their'}`;

    // Get visits for the next 30 days
    const visitWindow = useMemo(() => {
        const start = Date.now();
        return { start, end: start + FUTURE_VISIT_BOUNDARY };
    }, []);

    const { data: visits } = useGetVisitsQuery(
        {
            careRecipientRid: careRecipient?.teamMemberRid ?? '',
            startInstant: visitWindow.start,
            endInstant: visitWindow.end,
        },
        { skip: !careRecipient?.teamMemberRid }
    );

    const nextVisit = useMemo(() => {
        if (!visits) return undefined;
        return visits
            .filter(
                (visit) =>
                    visit.caregiverRid === currentUser?.teamMemberRid &&
                    visit.visitStatus === 'SCHEDULED'
            )
            .sort((a, b) => (a.visitStartInstant ?? 0) - (b.visitStartInstant ?? 0))[0];
    }, [visits, currentUser?.teamMemberRid]);

    const nextVisitDateRange = useMemo(() => formatVisitDateRange(nextVisit), [nextVisit]);

    return (
        <InnerMaxWidthContainer wrapperPropsSx={{ backgroundColor: 'common.white' }}>
            <Stack sx={{ gap: 2.5, alignItems: 'center' }}>
                <Box
                    sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <img src={HeartImage} alt="Heart" style={{ width: '400px' }} />
                    <DotLottieReact
                        src={confettiLottie}
                        autoplay
                        style={{
                            position: 'absolute',
                            top: '-25%',
                            width: '150%',
                            height: '150%',
                        }}
                    />
                </Box>
                <Stack sx={{ gap: 1.5, alignItems: 'center', textAlign: 'center' }}>
                    <Typography variant="h1" sx={{ fontWeight: 600 }}>
                        Thank you for your work, {nameSuffix}!
                    </Typography>
                    <Typography variant="body1">
                        Your notes have been sent to {careRecipientPossessive} family.
                    </Typography>

                    <Typography variant="body1">
                        {' '}
                        Thanks for being there for {careRecipientName} — your support makes a real
                        difference.
                    </Typography>
                </Stack>
                <Stack
                    sx={{
                        alignItems: 'center',
                        py: 2.5,
                        borderTop: `1px solid ${palette.gray?.[400]}`,
                        width: '100%',
                    }}
                >
                    {nextVisit ? (
                        <Stack sx={{ gap: 1, alignItems: 'center' }}>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                Next visit for {careRecipientName}
                            </Typography>
                            <Stack
                                sx={{
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    textAlign: 'center',
                                    width: 'max-content',
                                    gap: 1,
                                }}
                            >
                                <FontAwesomeIcon icon={faCalendar} />
                                <Typography variant="body1" sx={{ textWrap: 'balance' }}>
                                    {nextVisitDateRange}
                                </Typography>
                            </Stack>
                        </Stack>
                    ) : (
                        <Typography variant="body1">
                            No upcoming visits for {careRecipientName}.
                        </Typography>
                    )}
                </Stack>
            </Stack>
            <PageFooter>
                <Button
                    variant="contained"
                    fullWidth
                    color="primary"
                    onClick={() => navigate({ to: '/' })}
                >
                    Return to Home Page
                </Button>
            </PageFooter>
        </InnerMaxWidthContainer>
    );
}
